import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function AdminResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token");
      nav("/admin/forgot-password");
    }
  }, [token, nav]);

  const strength = (p) => {
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s; // 0-5
  };
  const score = strength(pwd);
  const strengthLabel = ["Too short", "Weak", "Fair", "Good", "Strong", "Excellent"][score];
  const strengthColor = ["bg-rose-400", "bg-rose-400", "bg-amber-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"][score];

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters");
    if (pwd !== pwd2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/admin/reset-password", { token, new_password: pwd });
      toast.success("Password updated — you can now sign in");
      nav("/admin/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Set new admin password" noindex />
      <div
        className="min-h-screen flex items-center justify-center px-5 py-10"
        style={{ background: "#F3F4F6" }}
        data-testid="admin-reset-page"
      >
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-[28px] px-8 sm:px-10 py-12 text-center"
          style={{ boxShadow: "0 24px 60px -20px rgba(15, 23, 42, 0.18), 0 8px 20px -12px rgba(15, 23, 42, 0.10)" }}
        >
          <div
            className="mx-auto w-20 h-20 rounded-[22px] flex items-center justify-center text-white"
            style={{
              background: "linear-gradient(135deg, #C026D3 0%, #DB2777 40%, #F97316 100%)",
              boxShadow: "0 14px 32px -10px rgba(219, 39, 119, 0.55)",
            }}
          >
            <KeyRound className="w-9 h-9" strokeWidth={2.5} />
          </div>

          <h1 className="mt-7 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Set new password
          </h1>
          <p className="mt-3 text-slate-500 text-base">
            Choose a strong password — at least 8 characters.
          </p>

          {/* Password 1 */}
          <div className="mt-10 text-left">
            <label className="block text-base font-medium text-slate-900 mb-2">New password</label>
            <div
              className={`flex items-center gap-2 rounded-2xl border-2 px-5 py-4 transition-colors ${
                pwd ? "border-sky-400" : "border-slate-200 focus-within:border-sky-400"
              }`}
            >
              <input
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                autoFocus
                data-testid="reset-pwd1"
                className="flex-1 bg-transparent outline-none text-base text-slate-900 placeholder:text-slate-400"
              />
              <button type="button" onClick={() => setShow((v) => !v)} className="text-slate-400 hover:text-slate-700 p-1">
                {show ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            {/* Strength meter */}
            {pwd && (
              <div className="mt-2 flex items-center gap-2" data-testid="reset-strength">
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${strengthColor}`} style={{ width: `${(score / 5) * 100}%` }} />
                </div>
                <span className="text-xs text-slate-500 font-medium">{strengthLabel}</span>
              </div>
            )}
          </div>

          {/* Password 2 */}
          <div className="mt-5 text-left">
            <label className="block text-base font-medium text-slate-900 mb-2">Confirm password</label>
            <div
              className={`flex items-center gap-2 rounded-2xl border-2 px-5 py-4 transition-colors ${
                pwd2 && pwd2 !== pwd
                  ? "border-rose-400"
                  : pwd2
                  ? "border-emerald-400"
                  : "border-slate-200 focus-within:border-sky-400"
              }`}
            >
              <input
                type={show ? "text" : "password"}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
                data-testid="reset-pwd2"
                className="flex-1 bg-transparent outline-none text-base text-slate-900 placeholder:text-slate-400"
              />
            </div>
            {pwd2 && pwd2 !== pwd && (
              <p className="mt-1.5 text-xs text-rose-600 text-left">Passwords don't match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || pwd.length < 8 || pwd !== pwd2}
            data-testid="reset-submit"
            className="mt-9 w-full inline-flex items-center justify-center gap-3 h-16 rounded-full text-white text-lg font-semibold transition-all disabled:opacity-50"
            style={{ background: "#0F172A", boxShadow: "0 12px 28px -10px rgba(15, 23, 42, 0.45)" }}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Updating…
              </span>
            ) : (
              <>
                <KeyRound className="w-5 h-5" /> Update password
              </>
            )}
          </button>

          <div className="mt-5">
            <Link
              to="/admin/login"
              className="text-sm text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to login
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}
